import { TableColumns, Table, Button, EditIcon } from '@bp/ui-components';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import dayjs from 'dayjs';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { LicensedProductTypes, Products } from 'pages/Settings/subpages/types';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { bookedBBBSlots } from 'utils/bookedBBBSlots';

const BookingsTableFieldsDocument = graphql(`
  fragment BookingsTableFields on Query {
    ...PermissionCheckerFields
    organizations(where: { uuid: $organizationUuid }) {
      uuid
      __typename
      licensedProducts {
        __typename
        ... on Bildungsplattform {
          uuid
          organizationConnection {
            edges {
              properties {
                notAfter
                notBefore
                version
              }
            }
          }
        }
        ... on Nextcloud {
          uuid
          profilesAggregate(where: { NOT: { email_IN: ["admin@tks.eu"] } }) {
            __typename
            count
          }
          organizationConnection {
            edges {
              properties {
                notAfter
                notBefore
                version
              }
            }
          }
        }
        ... on ThreemaBroadcast {
          uuid
          organizationConnection {
            edges {
              properties {
                notAfter
                notBefore
                version
              }
            }
          }
        }
        ... on Threema {
          uuid
          organizationConnection {
            edges {
              properties {
                notAfter
                notBefore
                version
              }
            }
          }
        }
        ... on BigBlueButton {
          uuid
          organizationConnection {
            edges {
              properties {
                notAfter
                notBefore
                version
              }
            }
          }
        }
        ... on Zoom {
          uuid
          organizationConnection {
            edges {
              properties {
                notAfter
                notBefore
                version
              }
            }
          }
        }
      }
    }
  }
`);

type BookingsTableType = {
  module: LicensedProductTypes;
  start: string;
  end: string;
  productVersion: string;
  usage: string;
};

type BookingsTableProps = {
  onEdit: (module: LicensedProductTypes) => void;
  query: FragmentType<typeof BookingsTableFieldsDocument> | undefined;
};

export const BookingsTable = ({ onEdit, query }: BookingsTableProps) => {
  const { t } = useTranslation();
  const fragment = getFragmentData(BookingsTableFieldsDocument, query);
  const licensedProducts = fragment?.organizations[0]?.licensedProducts;
  const permissions = usePermissionChecker(fragment);
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const { organizationProducts } = useContext(OrganizationConfigContext);

  const bildungsplattform = organizationProducts?.find((p) => p.__typename === 'Bildungsplattform');
  const bildungsplattformLicense = bildungsplattform?.organizationConnection.edges[0]?.properties.version;

  const possibleLicensedProducts: BookingsTableType[] = useMemo(() => {
    const possibleLicensedProducts = Object.keys(Products).map((product) => {
      return {
        __typename: Products[product as keyof typeof Products],
      };
    });

    return possibleLicensedProducts
      .map((product) => {
        const booked = licensedProducts?.find((d) => d.__typename === product.__typename);

        const start = booked?.organizationConnection?.edges[0]?.properties.notBefore;
        const end = booked?.organizationConnection?.edges[0]?.properties.notAfter;

        let usage = '';

        if (booked?.__typename === 'Nextcloud') {
          usage = booked?.profilesAggregate?.count?.toString() ?? '';
        }

        return {
          module: product.__typename as LicensedProductTypes,
          start: start ? dayjs(start).format('DD.MM.YYYY') : '',
          end: booked ? dayjs(end).format('DD.MM.YYYY') : '',
          productVersion: booked?.organizationConnection?.edges[0]?.properties.version?.toLowerCase() ?? '-',
          usage,
        };
      })
      .sort((a, b) => a.module.localeCompare(b.module));
  }, [licensedProducts]);

  const columns = useMemo((): TableColumns<BookingsTableType>[] => {
    return [
      {
        id: 'module',
        header: t('institution.module'),
        accessorKey: 'module',
        canExpand: true,
      },
      {
        id: 'start',
        header: t('institution.bookedDate'),
        accessorKey: 'start',
        size: 100,
        alignment: 'right',
      },
      {
        id: 'end',
        header: t('common.activeUntil'),
        accessorKey: 'end',
        size: 100,
        alignment: 'right',
      },
      {
        id: 'booked',
        accessorKey: 'licenseCount',
        header: t('institution.booked'),
        cell: ({ row }) => {
          const productVersion = row.original.productVersion;

          // TODO: other products
          if (row.original.module === 'Nextcloud') {
            return row.original.usage;
          }
          if (row.original.module === 'Bildungsplattform') {
            return t('app.' + bildungsplattformLicense);
          }
          if (row.original.module === 'BigBlueButton') {
            const slots = bookedBBBSlots(productVersion);
            if (slots === 0) {
              return 'SelfHosted';
            }
            return isNaN(slots) ? '-' : slots;
          }
          return '-';
        },
        size: 100,
        alignment: 'right',
      },
      {
        id: 'billable',
        accessorKey: 'billable',
        header: t('institution.billable'),
        size: 100,
        alignment: 'right',
        cell: ({ row }) => {
          if (row.original.module === 'Nextcloud') {
            return row.original.usage;
          }
          const productVersion = row.original.productVersion;
          const slots = bookedBBBSlots(productVersion);
          return slots === 0 || isNaN(slots) ? '-' : slots;
        },
      },
      {
        id: 'usage',
        accessorKey: 'usage',
        header: t('institution.usage'),
        size: 100,
        alignment: 'right',
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Table<BookingsTableType>
      columns={columns}
      data={possibleLicensedProducts}
      breakpoint='580px'
      customPadding='var(--spacing-6)'
      lastCol={(row) => {
        return (row.original.module === 'Bildungsplattform' &&
          permissions?.canUpdateBildungsplattform({ uuid: organizationUuid })) ||
          (row.original.module === 'BigBlueButton' && permissions?.canUpdateBBB({ uuid: organizationUuid })) ||
          (row.original.module === 'Zoom' && permissions?.canUpdateZoom({ uuid: organizationUuid })) ||
          (row.original.module === 'Threema' && permissions?.canUpdateThreema({ uuid: organizationUuid })) ||
          (row.original.module === 'ThreemaBroadcast' &&
            permissions?.canUpdateThreemaBroadcast({ uuid: organizationUuid })) ||
          (row.original.module === 'Nextcloud' && permissions?.canUpdateNextcloud({ uuid: organizationUuid })) ? (
          <Button hierarchy='ghost' icon={<EditIcon />} onClick={() => onEdit(row.original.module)} />
        ) : undefined;
      }}
    />
  );
};
